import React, { useState, useEffect } from 'react';
import { 
  PaperClipIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { 
  getAttachments, 
  deleteAttachment, 
  getAttachmentUrl, 
  formatFileSize, 
  getFileIcon,
  isImageFile 
} from '../../lib/attachments';
import { Attachment } from '../../types';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

interface AttachmentListProps {
  ideaId?: string;
  taskId?: string;
  onAttachmentDeleted?: () => void;
  showUploader?: User;
  className?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

const AttachmentList: React.FC<AttachmentListProps> = ({ 
  ideaId, 
  taskId, 
  onAttachmentDeleted,
  showUploader = undefined,
  className = '' 
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  // Load attachments on mount and when dependencies change
  useEffect(() => {
    loadAttachments();
  }, [ideaId, taskId]);

  const loadAttachments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!ideaId && !taskId) {
        setAttachments([]);
        return;
      }

      const data = await getAttachments(ideaId, taskId);
      setAttachments(data);
    } catch (err: any) {
      console.error('Failed to load attachments:', err);
      setError(err.message || 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    try {
      setDeletingIds(prev => new Set(prev).add(attachment.id));
      
      await deleteAttachment(attachment);
      
      // Remove from local state
      setAttachments(prev => prev.filter(a => a.id !== attachment.id));
      
      toast.success(`🗑️ ${attachment.fileName} deleted successfully`);
      onAttachmentDeleted?.();
      
    } catch (err: any) {
      console.error('Failed to delete attachment:', err);
      toast.error(`❌ Failed to delete ${attachment.fileName}: ${err.message}`);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const handleDeleteConfirm = (attachmentId: string) => {
    setConfirmingDelete(attachmentId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmingDelete) return;
    
    const attachment = attachments.find(a => a.id === confirmingDelete);
    if (!attachment) return;

    setConfirmingDelete(null);
    await handleDelete(attachment);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  const handlePreview = async (attachment: Attachment) => {
    try {
      const url = await getAttachmentUrl(attachment.filePath);
      setPreviewUrl(url);
      setPreviewAttachment(attachment);
    } catch (err: any) {
      console.error('Failed to get preview URL:', err);
      toast.error(`❌ Failed to preview ${attachment.fileName}: ${err.message}`);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const url = await getAttachmentUrl(attachment.filePath);
      
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`📥 Downloading ${attachment.fileName}...`);
    } catch (err: any) {
      console.error('Failed to download attachment:', err);
      toast.error(`❌ Failed to download ${attachment.fileName}: ${err.message}`);
    }
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewAttachment(null);
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-6 ${className}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-teal"></div>
        <span className="ml-2 text-sm text-gray-600">Loading attachments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-sm text-red-700">Error loading attachments: {error}</p>
        <button
          onClick={loadAttachments}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className={`text-center p-6 text-gray-500 ${className}`}>
        <PaperClipIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p className="text-sm">No attachments yet</p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <h4 className="text-sm font-medium text-gray-700 flex items-center">
        <PaperClipIcon className="h-4 w-4 mr-1" />
        Attachments ({attachments.length})
      </h4>

      <div className="space-y-2">
        {attachments.map((attachment) => {
          const isDeleting = deletingIds.has(attachment.id);
          const fileIcon = getFileIcon(attachment.mimeType);
          const isImage = isImageFile(attachment.mimeType);

          return (
            <div
              key={attachment.id}
              className={`
                bg-white border border-gray-200 rounded-lg p-3 transition-all duration-200
                ${isDeleting ? 'opacity-50' : 'hover:shadow-sm hover:border-gray-300'}
              `}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* File Icon */}
                  <div className="flex-shrink-0 text-lg">
                    {fileIcon}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h5 className="text-sm font-medium text-gray-900 truncate">
                        {attachment.fileName}
                      </h5>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatFileSize(attachment.fileSize)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="h-3 w-3" />
                        <span>{format(attachment.uploadedAt, 'MMM d, yyyy')}</span>
                      </div>
                      
                      {showUploader && (
                        <div className="flex items-center space-x-1">
                          <UserIcon className="h-3 w-3" />
                          <span>Uploaded by user</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {/* Preview Button (for images) */}
                  {isImage && (
                    <button
                      onClick={() => handlePreview(attachment)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Preview image"
                      disabled={isDeleting}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                  )}

                  {/* Download Button */}
                  <button
                    onClick={() => handleDownload(attachment)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Download file"
                    disabled={isDeleting}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteConfirm(attachment.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete attachment"
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Preview Modal */}
      {previewUrl && previewAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {previewAttachment.fileName}
              </h3>
              <button
                onClick={closePreview}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <img
                src={previewUrl}
                alt={previewAttachment.fileName}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {formatFileSize(previewAttachment.fileSize)} • {format(previewAttachment.uploadedAt, 'PPP')}
              </div>
              <button
                onClick={() => handleDownload(previewAttachment)}
                className="btn-outline text-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmingDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Attachment
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete "{attachments.find(a => a.id === confirmingDelete)?.fileName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentList; 